// React Compiler opt-out: this file still reads mutable external state during render
// (legacy store object getters and/or current-time reads) that the useFluxStore bridge
// doesn't cover. Remove after migrating those reads (see flux-bridge-conversion skill).
'use no memo';

import classnames from 'classnames';
import React, {useContext, useMemo, useRef, useState, type JSX} from 'react';
import type {WithRouterProps} from 'react-router';
import {withRouter} from 'react-router';

import {Footer, IconButton} from '@albert-io/atomic';
import sessionStore from 'client/Session/SessionStore';
import TitleBar from 'components/PracticeView/TitleBar/TitleBar.react';
import {mergeRefs} from 'lib/reactUtils';

import type {FrqGrading} from '../QuestionTypes/FreeResponse/frqGradingStatus';
import {useShowTranslationToggle} from '../QuestionTypes/shared/TranslationSection';
import {AnnotationAnnouncer} from './Annotations/shared/AnnotationAnnouncer';
import AnnotationFreezeShield from './Annotations/AnnotationFreezeShield/AnnotationFreezeShield';
import AnnotationNoteIndicators from './Annotations/AnnotationNoteIndicators/AnnotationNoteIndicators';
import AnnotationNotesPanel from './Annotations/AnnotationNotesPanel/AnnotationNotesPanel';
import {ANNOTATION_GUTTER_PROPS} from './Annotations/shared/annotationGutter';
import EmptyQuestionShell from './EmptyQuestionShell/EmptyQuestionShell';
import FusedPanes from './FusedPanes/FusedPanes.react';
import {MobilePracticeViewContext} from './MobilePracticeViewProvider.context';
import {useOptionEliminatorReset} from './OptionEliminatorStore/OptionEliminatorStore';
import {PRACTICE_MAIN_MIN_WIDTH_CLASS, type QuestionAreaMin} from './practiceViewLayout';
import './practice-view.scss';
import {
  PracticeViewWrapper,
  QUESTION_AREA_PROPS,
  QUESTION_NAV_PROPS
} from './PracticeViewWrapper/PracticeViewWrapper';
import QuestionRenderer from './QuestionRenderer/QuestionRenderer.react';
import QuestionsList from './QuestionsList/QuestionsList.react';
import type {QuestionListProps, QuestionProps} from './QuestionsList/questionsList.types';

interface PracticeViewProps extends WithRouterProps {
  removeQuestionListOnMobile?: boolean; // needed for the time being to conditionally remove questionlist since its not needed on mobile view TODO remove after moving question list into tool bar for AssignmentPracticeView
  activeQuestion: any; // PropTypes.instanceOf(getModelForResourceType('question_v3')), TODO-KBC: Build typescript methods
  activeQuestionSet: any; // PropTypes.instanceOf(getModelForResourceType('question_set_v1')), TODO-KBC: Build typescript methods
  activeQuestionStore: any; // PropTypes.instanceOf(QuestionTypeStore), TODO-KBC: Build typescript methods
  activeGuess: any; // PropTypes.instanceOf(getModelForResourceType('guess_v1')), TODO-KBC: Build typescript methods
  activeSubject: any; // PropTypes.instanceOf(getModelForResourceType('subject_v2')), TODO-KBC: Build typescript methods
  questionProps?: QuestionProps;
  questionRendererProps: {
    actions: React.ReactNode;
    rendererType: 'PreGuess' | 'PostGuess';
    interactive?: boolean;
    navigateBackProps: {
      disabled: boolean;
      handleNavigation?: () => void;
    } | null;
    navigateNextProps: {
      disabled: boolean;
      handleNavigation?: () => void;
    } | null;
    headingActions: React.ReactNode;
    modifyTitle?: () => {};
    /** Puts the prompt under a "Prompt" heading that collapses it. */
    collapsiblePrompt?: boolean;
    /** Set where the response belongs to someone else — a teacher reading a student's submission. */
    isViewingStudentWork?: boolean;
    /** Free response only: the viewed guess's grading state, where a teacher-gated read supplies one. */
    frqGrading?: FrqGrading;
  };
  questionSetProps?: Object;
  questionSets: any; // ImmutablePropTypes.listOf(PropTypes.instanceOf(getModelForResourceType('question_set_v1'))), TODO-KBC: Build typescript methods
  questionsListProps?: QuestionListProps;
  stageComponent?: React.ReactNode;
  showDifficulty?: boolean;
  /**
   * Reserves the margin the note indicators are drawn in. The surface passes its
   * own availability (flag, submission, device), since only it can know — and
   * elsewhere the gutter would narrow the question for an unreachable feature.
   */
  supportsAnnotations?: boolean;
  titleBar?: React.ReactElement<any>;
  isLoading: boolean;
  toolbar?: React.ReactNode;
  /** A strip under the title bar and toolbar, for a notice that spans the view. */
  notice?: React.ReactNode;
  activeQuestionDisplayOrientation?: 'default' | 'vertical' | 'horizontal';
  hasQuestionSetAccessPredicate?: (questionSet: any) => boolean;
  QuestionListComponent?: React.ReactElement<any>;
  partialCreditEnabled?: boolean;
  /**
   * When true, the question list, question area, and right-side panel share a
   * single bordered card, split by resize rails, instead of separate panels.
   */
  fusedLayout?: boolean;
  /**
   * When true, the question list is hidden and a floating expand button is
   * rendered at the top-left of the question area.
   */
  isQuestionListCollapsed?: boolean;
  /** Handler for the floating expand button. */
  onExpandQuestionList?: () => void;
  /** Fused layout: pixels the question list opens at. */
  questionListDefaultWidth?: number;
  /** Fused layout: pixels the question list can be dragged down to, in place of its 20% floor. */
  questionListMinWidth?: number;
  /**
   * Always-mounted content for the fused layout's right-side panel. The
   * `isRightSidePanelOpen` prop controls its visibility so it can animate.
   */
  rightSidePanel?: React.ReactNode;
  /** Controls visibility of the right-side panel. Defaults to false. */
  isRightSidePanelOpen?: boolean;
  /**
   * Optional content floated over the bottom-right of the question area (e.g.
   * prev/next question navigation in the FRQ grading layout). Inert when unset.
   */
  questionNavOverlay?: React.ReactNode;
  /** Classes for the view's outer wrapper, e.g. to make room for a docked calculator. */
  className?: string;
  /** From the layout toggle: lets Automatic measure the question area. */
  questionAreaRef?: (element: HTMLElement | null) => void;
  /**
   * From the layout toggle: the question area's minimum for the chosen layout. Past
   * it, the view scrolls sideways instead of crushing the question.
   */
  questionAreaMin?: QuestionAreaMin;
  /** For hosts that remount the practice view per question: focuses the question area on mount. */
  focusQuestionOnMount?: boolean;
}

export const hasQuestionSetAccessPredicateDefaultValue = (questionSet) => {
  const subject = questionSet.getSubject();
  const isRestricted = subject.getMeta().hasRestrictedFreePractice();
  if (sessionStore.isStudent() && isRestricted) {
    return false;
  }
  return (
    (subject.getMeta().isUserHasAccess() as boolean) || (questionSet.getMeta().isFree() as boolean)
  );
};

const PracticeView = ({
  removeQuestionListOnMobile = false,
  activeQuestion,
  activeQuestionSet,
  activeQuestionStore,
  activeGuess,
  activeSubject,
  activeQuestionDisplayOrientation = 'default',
  questionProps = {
    onQuestionClick: () => {}
  },
  // @ts-ignore - yelling b/c object doesn't have correct props initially
  questionRendererProps = {},
  questionSetProps = {},
  questionsListProps,
  questionSets,
  stageComponent,
  showDifficulty = true,
  supportsAnnotations = false,
  titleBar,
  isLoading,
  params,
  toolbar,
  notice,
  hasQuestionSetAccessPredicate = hasQuestionSetAccessPredicateDefaultValue,
  QuestionListComponent,
  partialCreditEnabled = false,
  fusedLayout = false,
  isQuestionListCollapsed = false,
  onExpandQuestionList,
  questionListDefaultWidth,
  questionListMinWidth,
  rightSidePanel,
  isRightSidePanelOpen = false,
  questionNavOverlay,
  className,
  questionAreaRef,
  questionAreaMin,
  focusQuestionOnMount
}: PracticeViewProps) => {
  useOptionEliminatorReset();

  const {studentId} = params;

  const showToggle = useShowTranslationToggle();
  // Container refs for handling scrolling on question updates
  const mainWrapperRef = useRef<HTMLDivElement>(null);
  // Memoized: a new callback ref every render would detach and re-attach the node,
  // resetting the layout toggle's measurement each time.
  const questionAreaNodeRef = useMemo(
    () => mergeRefs(mainWrapperRef, questionAreaRef),
    [questionAreaRef]
  );
  const hasQuestionAreaMin = !fusedLayout && (questionAreaMin?.width ?? 0) > 0;
  const answerInputPaneRef = useRef<HTMLDivElement>(null);
  const stackedContainerRef = useRef<HTMLDivElement>(null);
  const postSubmissionScrollPointRef = useRef<HTMLDivElement>(null);
  const {isMobile, isQuestionsListInView, setIsQuestionsListInView} =
    useContext(MobilePracticeViewContext);

  // controlling the position of the question resizer to persist the position when switching between questions
  const [activeQuestionResizerPosition, setActiveQuestionResizerPosition] = useState<
    number | undefined
  >(undefined);

  const makeMobileOnQuestionClick = (args) => {
    return (e?: React.MouseEvent) => {
      if (isMobile && isQuestionsListInView) {
        setIsQuestionsListInView(false);
      }
      questionProps.onQuestionClick(...args)(e);
    };
  };

  let QuestionListToRender: JSX.Element | null = null;
  if (QuestionListComponent) {
    QuestionListToRender = QuestionListComponent;
  } else if (questionsListProps) {
    QuestionListToRender = (
      <QuestionsList
        {...questionsListProps!}
        className={classnames('practice-view__questions-list flex flex-col', {
          'practice-view__questions-list--in-view': isQuestionsListInView
        })}
        activeQuestion={activeQuestion}
        questionSetProps={{
          ...questionSetProps,
          hasQuestionSetAccessPredicate
        }}
        questionProps={{
          ...questionProps,
          showDifficulty,
          onQuestionClick: (...args) => makeMobileOnQuestionClick(args)
        }}
        questionSets={questionSets}
        isLoading={isLoading}
      />
    );
  }

  const questionArea = (
    <div
      className={classnames('practice-view__question-area min-w-0 relative grow overflow-auto', {
        'practice-view__question-area--translation-enabled': showToggle,
        // Zero out the legacy 8px top/bottom padding from
        // practice-view.scss so the question column aligns with
        // the question list column inside the fused card.
        'pt-x0! pb-x0!': fusedLayout,
        // Matches the list and side panel headers inside the fused card.
        '[&_.q-heading-block]:h-x8': fusedLayout
      })}
      ref={questionAreaNodeRef}
      {...QUESTION_AREA_PROPS}
      data-testid='practice-view__question-area'
      {...(supportsAnnotations ? ANNOTATION_GUTTER_PROPS : {})}
    >
      <AnnotationFreezeShield targetRef={mainWrapperRef} />
      <AnnotationNoteIndicators />
      <div
        className={classnames('practice-view__question-area h-full grow', {
          'practice-view__question-area--dim': isMobile && isQuestionsListInView,
          'mb-x5': isMobile && removeQuestionListOnMobile, // needed since new sticky question list footer on AssignmentPracticeView cuts footer disclaimer off
          'practice-view__mobile-redesign': activeQuestionDisplayOrientation !== 'default',
          'pt-x0! pb-x0!': fusedLayout
        })}
      >
        {activeQuestion ? (
          <QuestionRenderer
            {...questionRendererProps}
            activeQuestion={activeQuestion}
            activeQuestionSet={activeQuestionSet}
            activeQuestionStore={activeQuestionStore}
            activeGuess={activeGuess}
            activeSubject={activeSubject}
            answerInputPaneRef={answerInputPaneRef}
            displayOrientation={activeQuestionDisplayOrientation}
            isMobile={isMobile}
            onResize={(position) => setActiveQuestionResizerPosition(position)}
            showDifficulty={showDifficulty}
            resizerPosition={activeQuestionResizerPosition}
            stackedContainerRef={stackedContainerRef}
            postSubmissionScrollPointRef={postSubmissionScrollPointRef}
            hasQuestionSetAccessPredicate={hasQuestionSetAccessPredicate}
            partialCreditEnabled={partialCreditEnabled}
            // The fused header is a fixed height, so a wrapped title spills out of it.
            truncateTitle={fusedLayout}
            leadingHeaderAction={
              fusedLayout && isQuestionListCollapsed && onExpandQuestionList ? (
                <IconButton
                  icon={['far', 'sidebar']}
                  label='Show question list'
                  onClick={onExpandQuestionList}
                  size='s'
                />
              ) : null
            }
          />
        ) : (
          <EmptyQuestionShell />
        )}
        {activeQuestionDisplayOrientation === 'default' && !fusedLayout && <Footer minimal />}
        {/* Room to scroll the last line clear of the floating nav (40px buttons, 16px inset). */}
        {questionNavOverlay && <div aria-hidden className='h-x9' />}
      </div>
    </div>
  );

  const questionNavOverlayToRender = questionNavOverlay ? (
    <div className='contents' {...QUESTION_NAV_PROPS}>
      {questionNavOverlay}
    </div>
  ) : null;

  const showQuestionList = (!isMobile || !removeQuestionListOnMobile) && QuestionListToRender;

  return (
    <PracticeViewWrapper
      activeQuestion={activeQuestion}
      questionRendererType={questionRendererProps?.rendererType}
      studentId={studentId}
      mainWrapperRef={mainWrapperRef}
      displayOrientation={activeQuestionDisplayOrientation}
      answerInputPaneRef={answerInputPaneRef}
      stackedContainerRef={stackedContainerRef}
      postSubmissionScrollPointRef={postSubmissionScrollPointRef}
      className={className}
      focusQuestionOnMount={focusQuestionOnMount}
    >
      <div className='practice-view__title-bar'>
        {titleBar &&
          React.cloneElement(
            titleBar,
            isMobile && !isQuestionsListInView && !stageComponent
              ? {
                  imgHref: null,
                  title: 'Questions',
                  backButton: (
                    <TitleBar.BackButton
                      as='button'
                      label='Questions'
                      icon='chevron-left'
                      onClick={() => setIsQuestionsListInView(true)}
                    />
                  )
                }
              : {}
          )}
        {toolbar}
        {notice}
        <AnnotationAnnouncer />
      </div>
      <div
        className={classnames(
          'practice-view__main-wrapper background-subtle flex grow',
          // With a minimum, overflow scrolls instead of clipping the notes panel;
          // safe centering keeps the start of the overflow reachable.
          hasQuestionAreaMin
            ? 'justify-center-safe overflow-x-auto overflow-y-hidden'
            : 'justify-center overflow-hidden'
        )}
      >
        {stageComponent ? (
          <div className='practice-view__stage-component-scroll-handler background-inset absolute w-full overflow-auto'>
            <div className='practice-view__stage-component-wrapper mx-auto'>
              {stageComponent}
              <Footer minimal />
            </div>
          </div>
        ) : fusedLayout ? (
          // Match the Figma's 16px inset around the fused card. `!` beats the
          // `.practice-view__main { padding: 0 16px }` shorthand in practice-view.scss.
          <div className='practice-view__main py-x2! grid grow grid-rows-[100%] overflow-hidden'>
            <FusedPanes
              list={showQuestionList ? QuestionListToRender : null}
              isListOpen={!isQuestionListCollapsed}
              question={questionArea}
              questionOverlay={questionNavOverlayToRender}
              panel={rightSidePanel}
              isPanelOpen={isRightSidePanelOpen}
              listDefaultWidth={questionListDefaultWidth}
              listMinWidth={questionListMinWidth}
            />
          </div>
        ) : (
          <div
            className={classnames('practice-view__main flex grow overflow-hidden', {
              [PRACTICE_MAIN_MIN_WIDTH_CLASS[questionAreaMin?.width ?? 0] ?? '']: hasQuestionAreaMin
            })}
          >
            <div
              className={classnames('min-w-0 flex grow overflow-hidden', {
                relative: !!questionNavOverlay
              })}
            >
              {showQuestionList && !isQuestionListCollapsed && QuestionListToRender}
              {questionArea}
              {questionNavOverlayToRender && (
                <div className='right-x2 bottom-x2 z-raised absolute'>
                  {questionNavOverlayToRender}
                </div>
              )}
            </div>
          </div>
        )}
        {/* Sibling of `practice-view__main`, so it escapes that box's 16px page
            padding and sits flush against the content region's right edge. The
            fused layout owns its own right slot, so it keeps it. */}
        {!fusedLayout && !stageComponent && (
          <AnnotationNotesPanel questionId={activeQuestion?.getId()} />
        )}
      </div>
    </PracticeViewWrapper>
  );
};

export default withRouter(PracticeView);
